import { css } from 'styled-components';

import { media, VALID_BUTTON_STYLE } from '../../lib/StyledComponents';

import { IButtonProps } from './common';

/* stylelint-disable declaration-colon-newline-after */
const ButtonInlineStyles = css`
  ${({ theme: { button } }) => css`
    ${media.small`
      width: auto;
      min-width: ${button.baseButtonMinWidth};
      display: inline-block;
    `}

    > * {
      display: inline-block;
    }
  `};
`;
/* stylelint-enable declaration-colon-newline-after */

const DEFAULT_BUTTON_STYLE: VALID_BUTTON_STYLE = 'primary';

const BaseButtonStyles = css<IButtonProps>`
  width: 100%;
  /* stylelint-disable declaration-colon-newline-after */
  padding: ${({ theme: { button }, size, buttonStyle }) =>
    size === 'tiny' ? button.tinyPadding : button[buttonStyle || DEFAULT_BUTTON_STYLE].padding
  };
  /* stylelint-enable */
  box-shadow: ${({theme: { button }, buttonStyle }) => button[buttonStyle || DEFAULT_BUTTON_STYLE].boxShadow};
  display: block;
  position: relative;
  border-radius: ${({ theme: { button } }) => button.borderRadius};
  border: 0;
  line-height: 1.2;
  transition: color 100ms ease-out, background 100ms ease-out;
  text-align: center;
  text-transform: ${({ theme: { button } }) => button.textTransform};
  font-size: ${({ theme: { button }, size }) => size === 'tiny' ? button.tinySize : button.fontSize};
  font-weight: ${({ theme: { button } }) => button.fontWeight};
  letter-spacing: ${({ theme: { button }}) => button.letterSpacing};
  cursor: ${({ disabled }) => disabled ? 'default' : 'pointer'};

  span,
  svg {
    display: flex;
    align-items: center;
    justify-content: center;
  }

  ${props => props.inline ? ButtonInlineStyles : ''}
`;

export const PrimaryDisabled = css<IButtonProps>`
  background: ${({ theme: { button: { primary } } }) => primary.colorBackgroundDisabled };
  color: ${({ theme: { button: { primary } } }) => primary.colorTextDisabled};
  border: 1px solid ${({ theme: { button: { primary } } }) => primary.colorBorderDisabled};
  cursor: not-allowed;

  svg {
    fill: CurrentColor;
  }
`;

export const PrimaryActive = css<IButtonProps>`
  background: ${({ theme: { button: { primary } } }) => primary.colorBackground};
  border: 1px solid ${({ theme: { button: { primary } } }) => primary.colorBorder};
  color: ${({ theme: { button: { primary } } }) => primary.colorText};

  ${({ isLoading }) => !isLoading && css`
    &:hover {
      background: ${({ theme: { button: { primary } } }) => primary.colorBackgroundHover};
      border: 1px solid ${({ theme: { button: { primary } } }) => primary.colorBorderHover};
      color: ${({ theme: { button: { primary } } }) => primary.colorTextHover};
    }

    &:focus,
    &:active {
      background: ${({ theme: { button: { primary } } }) => primary.colorBackgroundFocus};
      border: 1px solid ${({ theme: { button: { primary } } }) => primary.colorBackgroundFocus};
      color: ${({ theme }) => theme.colorWhite};
    }
  `}
`;

export const PrimaryButton = css<IButtonProps>`
  ${({disabled}) => disabled
    ? PrimaryDisabled
    : PrimaryActive}
`;

export const SecondaryActive = css`
  background: ${({ theme: { button: { secondary } } }) => secondary.colorBackground};
  border: ${({ theme: { button } }) => `${button.borderWidth} solid ${button.secondary.colorBorder}`};
  color: ${({ theme: { button: { secondary } } }) => secondary.colorText};

  &:hover {
    background: ${({ theme: { button: { secondary } } }) => secondary.colorBackgroundHover};
    border: ${({ theme: { button } }) => `${button.borderWidth} solid ${button.secondary.colorBorderHover}`};
    color: ${({ theme: { button: { secondary } } }) => secondary.colorTextHover};
  }

  &:focus,
  &:active {
    background: ${({ theme: { button: { secondary } } }) => secondary.colorBackgroundFocus};
    border: ${({ theme: { button } }) => `${button.borderWidth} solid ${button.secondary.colorBorderHover}`};
    color: ${({ theme: { button: { secondary } } }) => secondary.colorTextHover};
  }

  &:hover,
  &:focus,
  &:active {
    svg {
      fill: CurrentColor;
    }
  }
`;

export const SecondaryDisabled = css`
  background: ${({ theme: { button: { secondary } } }) => secondary.colorBackgroundDisabled};
  color: ${({ theme: { button: { secondary } } }) => secondary.colorTextDisabled};
  border: ${({ theme: { button } }) => `${button.borderWidth} solid ${button.secondary.colorBorderDisabled}`};
  cursor: not-allowed;

  svg {
    fill: currentColor;
  }
`;

export const SecondaryButton = css<IButtonProps>`
  ${({disabled}) => disabled
    ? SecondaryDisabled
    : SecondaryActive
  }
`;

export const BaseButton = BaseButtonStyles;
